/* eslint-disable no-console */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';

import FilteredUnit from '../../constants/FilteredUnit';
import {SampleExcel} from '../../constants/SampleExcel';
import {ISelect} from '../../interface/ISelect';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import businessTravelAPI from '../../services/modules/IP/businessTravelAPI';
import frequencyJSON from '../../services/modules/JSON/frequency';
import modeOfTransportTravJSON from '../../services/modules/JSON/modeOfTransportByTravel';
import usePlatformStore from '../../store/platformStore/platformStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import {shouldBlockReadOnly} from '../../utils/SubscriptionHelper';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import BusinessForm from '../Forms/BusinessForm';
import {BusKey, BusLabel} from '../Forms/Constants/IP/businessKeyLabel';
import {
  facilityKeyLabel,
  yearKeyLabel
} from '../Forms/Constants/IP/commonKeyLabel';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {FullInfo, attachedDocumentColumn, makeCheckKeyLabel, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import {
  excludeAcceptedIdsForDelete,
  getBulkSelectedIdsInDataSource,
  useBulkCheckerModals
,
  createBulkRowSelection
} from './Utils/TableBulkSelectionBar';
import TableTitleHeader from './Utils/TableTitleHeader';

const BusTravelTable: React.FC<{
  selectedYear: string;
  facilityId: string;
}> = ({selectedYear, facilityId}) => {
  interface DataType {
    key: React.Key;
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    mobile: number;
    frequencyOfTravel: number;
    numberOfFrequency: number;
    class: number;
    mode: number;
    distanceTravelled: number;
    travelTo: string;
    travelFrom: string;
    cost: number;
    status: string;
    comment: string;
    documentUrl?: string;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [showModel, setShowModel] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const bulkSelectedIds = useMemo(
    () => getBulkSelectedIdsInDataSource(selectedRowKeys, dataSource),
    [selectedRowKeys, dataSource]
  );
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const {modeOfTransportTrav, frequency} = useJSONStore.getState();
  const [allJSON, setAllJSON] = useState(false);
  const [formSelectedCountry, setFormSelectedCountry] = useState<
    string | undefined
  >(undefined);

  const [showModelInfo, setShowModelInfo] = useState(false);
  const {selectedCountry, efdbData} = useContext(SelectedCountryContext);
  const [facilityEfdb, setFacilityEfdb] = useState<any>({});

  useEffect(() => {
    if (!facilityId || !selectedYear) {
      setDataSource([]);
    }
    if (facilityId && selectedYear && allJSON) {
      setIsRefresh(true);
      businessTravelAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${facilityId}/${selectedYear}`
      )
        .then((res) => {
          setDataSource(res ?? []);
          setFacilityEfdb(
            efdbData?.[selectedYear]?.find((i: any) => {
              return i.facilityId === facilityId;
            })
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [facilityId, selectedYear, allJSON]);

  useEffect(() => {
    // setIsRefresh(true);
    Promise.all([modeOfTransportTravJSON(), frequencyJSON()])
      .catch(() => {})
      .finally(() => {
        // setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      businessTravelAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${facilityId}/${selectedYear}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  const handleDelete = (_id: string[] | string) => {
    const ids = Array.isArray(_id) ? _id : [_id];
    const businesstravel = excludeAcceptedIdsForDelete(ids, dataSource);
    if (businesstravel.length === 0) {
      return;
    }
    businessTravelAPI(APITypeEnum.DELETE_ALL, {
      businesstravel
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  const bulkChecker = useBulkCheckerModals({
    selectedIds: bulkSelectedIds,
    refresh,
    selectionContextKey: `${facilityId}/${selectedYear}`,
    onSelectionContextChange: () => {
      setSelectedRowKeys([]);
    },
    getBulkDeleteEligibleCount: (ids) =>
      excludeAcceptedIdsForDelete(ids, dataSource).length,
    onSuccess: () => {
      setSelectedRowKeys([]);
      setRefresh(true);
    },
    onBulkStatusSubmit: async ({ids, status, comment}) =>
      await businessTravelAPI(APITypeEnum.STATUS_ALL, {
        businesstravel: ids,
        status,
        [checkerStatusKey.COMMENT]: comment
      }),
    onBulkDelete: async (ids) => {
      const businesstravel = excludeAcceptedIdsForDelete(ids, dataSource);
      if (businesstravel.length === 0) {
        return false;
      }

      return await businessTravelAPI(APITypeEnum.DELETE_ALL, {
        businesstravel
      });
    }
  });

  const handleSelectAll = () => {
    setSelectedRowKeys(dataSource.map((row) => row._id));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value: any, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const businessTravelKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: facilityKeyLabel.key,
      label: facilityKeyLabel.label,
      type: objTypes.facilityFormPreview,
      relatedKey: yearKeyLabel.key,
      isMaker: hasUserRole(UserRole.Maker_Role)
    },
    {
      key: BusKey.FIRST_NAME,
      label: BusLabel.FIRST_NAME,
      type: objTypes.string
    },
    {
      key: BusKey.LAST_NAME,
      label: BusLabel.LAST_NAME,
      type: objTypes.string
    },
    {
      key: BusKey.EMAIL,
      label: BusLabel.EMAIL,
      type: objTypes.string
    },
    {
      key: BusKey.MOBILE,
      label: BusLabel.MOBILE,
      type: objTypes.string
    },
    {
      key: BusKey.FREQUENCY_OF_TRAVEL,
      label: BusLabel.FREQUENCY_OF_TRAVEL,
      type: objTypes.json,
      format: frequency
    },
    {
      key: BusKey.NO_OF_FREQUENCY,
      label: BusLabel.NO_OF_FREQUENCY,
      type: objTypes.number
    },
    {
      key: BusKey.MODE,
      label: BusLabel.MODE,
      type: objTypes.json,
      format: modeOfTransportTrav
    },
    {
      key: BusKey.DISTANCE_TRAVELLED,
      label: BusLabel.DISTANCE_TRAVELLED,
      type: objTypes.number,
      unit: FilteredUnit(formSelectedCountry ?? selectedCountry).distance
    },
    {
      key: BusKey.FROM,
      label: BusLabel.FROM,
      type: objTypes.string
    },
    {
      key: BusKey.TO,
      label: BusLabel.TO,
      type: objTypes.string
    },
    {
      key: BusKey.COST,
      label: BusLabel.COST,
      type: objTypes.number,
      unit: FilteredUnit(formSelectedCountry ?? selectedCountry).currency,
      isCost: true
    },
    ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  const columns: Array<defaultCol & ColumnsType<DataType>[0]> = useMemo(() => {
    return [
      {
        title: BusLabel.FIRST_NAME,
        dataIndex: BusKey.FIRST_NAME,
        editable: true,
        // ...sorter(BusKey.FIRST_NAME as keyof DataType),
        // fixed: 'left',
        // width: '200px',
        ...SearchFilter(
          BusLabel.FIRST_NAME,
          BusKey.FIRST_NAME as keyof DataType
        )
      },
      {
        title: BusLabel.LAST_NAME,
        dataIndex: BusKey.LAST_NAME,
        // width: '200px',
        editable: true,
        // ...sorter(BusKey.LAST_NAME as keyof DataType),
        ...SearchFilter(BusLabel.LAST_NAME, BusKey.LAST_NAME as keyof DataType)
      },

      {
        title: BusLabel.EMAIL,
        dataIndex: BusKey.EMAIL,
        editable: true,
        // ...sorter(BusKey.EMAIL as keyof DataType),
        // width: '200px',
        ...SearchFilter(BusLabel.EMAIL, BusKey.EMAIL as keyof DataType)
      },
      {
        title: BusLabel.MOBILE,
        dataIndex: BusKey.MOBILE,
        editable: true,
        // width: '200px',
        type: 'number'
      },
      {
        title: BusLabel.FREQUENCY_OF_TRAVEL,
        dataIndex: BusKey.FREQUENCY_OF_TRAVEL,
        editable: true,
        // ...sorter(BusKey.FREQUENCY_OF_TRAVEL as keyof DataType),
        // width: '200px',
        type: 'select',
        filterer: frequency,
        filters: frequency?.map((item) => {
          return {text: item.label, value: item.value};
        }),
        onFilter: (value, record) => {
          return record[BusKey.FREQUENCY_OF_TRAVEL as keyof DataType] === value;
        },
        render: (value: any) => {
          return (
            <>
              {
                frequency?.find((item) => {
                  return item.value === value;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: BusLabel.NO_OF_FREQUENCY,
        dataIndex: BusKey.NO_OF_FREQUENCY,
        editable: true,
        // width: '200px',
        type: 'number'
      },
      {
        title: BusLabel.MODE,
        dataIndex: BusKey.MODE,
        editable: true,
        // ...sorter(BusKey.MODE as keyof DataType),
        // width: '200px',
        type: 'select',
        ...SearchFilter(BusLabel.MODE, BusKey.MODE, modeOfTransportTrav),
        render: (value) => {
          return (
            <>
              {
                modeOfTransportTrav?.find((item) => {
                  return (
                    item.value === value && item.dbName === facilityEfdb.scope3
                  );
                })?.label
              }
            </>
          );
        }
      },
      {
        title: BusLabel.DISTANCE_TRAVELLED,
        dataIndex: BusKey.DISTANCE_TRAVELLED,
        editable: true,
        type: 'number',
        width: '200px',
        render: (value) => {
          return value
            ? `${formatIndian(value)} ${FilteredUnit(selectedCountry).distance}`
            : '-';
        }
      },
      {
        title: BusLabel.FROM,
        dataIndex: BusKey.FROM,
        editable: true,
        width: '200px',
        render: (value) => {
          return value || '-';
        }
      },
      {
        title: BusLabel.TO,
        dataIndex: BusKey.TO,
        editable: true,
        width: '200px',
        render: (value) => {
          return value || '-';
        }
      },
      {
        title: BusLabel.COST,
        dataIndex: BusKey.COST,
        editable: true,
        type: 'number',
        width: '200px',
        render: (value) => {
          return (
            <>
              {value
                ? `${FilteredUnit(selectedCountry).currency} ${formatIndian(
                    value
                  )}`
                : '-'}
            </>
          );
        }
      },
      attachedDocumentColumn,
      ...(hasUserRole(UserRole.Maker_Role) ||
      hasUserRole(UserRole.Both_role) ||
      hasViewRole(UserRole.View_Only)
        ? [
            {
              title: makeCheckKeyLabel.STATUS_COMMENTS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <div
                    style={{
                      fontWeight: 700,
                      color: colorText.color,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}{' '}
                    {value === 'rejected' && record?.comment && (
                      <span className="customComment"> - {record.comment}</span>
                    )}
                  </div>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: makeCheckKeyLabel.STATUS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <Tag
                    onClick={() => {
                      if (shouldBlockReadOnly()) {
                        usePlatformStore
                          .getState()
                          .setOpenOnFreeTrialMessageModel(true);

                        return;
                      }

                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    color={colorText.color}
                    style={{
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}
                  </Tag>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: FullInfo.title,
              dataIndex: 'FullInfo',
              editable: true,
              type: 'select',
              render: (_: any, record: any) => {
                return (
                  <Button
                    onClick={() => {
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    shape="circle"
                    style={{
                      marginRight: 16,
                      cursor: 'pointer',
                      color: '#435B37',
                      background: '#F5FBF3',
                      border: 0
                    }}
                  >
                    <ExclamationOutlined />
                  </Button>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: Title.actionTitle,
              dataIndex: 'operation',
              type: '' as const,
              fixed: 'right' as const,
              width: '200px',
              render: (value: any, record: any) =>
                dataSource.length >= 1 ? (
                  <ActionsButton
                    record={record}
                    setShowModel={setShowModel}
                    setEditData={setEditData}
                    setIsEdit={setIsEdit}
                    handleDelete={handleDelete}
                  />
                ) : null
            }
          ]
        : [])
    ];
  }, [dataSource]);

  return (
    <div>
      <TableTitleHeader
        title="Business Travel Info"
        filter={{facilityId, selectedYear}}
        addButton={{
          setShowModel,
          setIsEdit,
          buttonTitle: 'Add Travel'
        }}
        sampleDownload={{href: SampleExcel.businessTravel}}
        jobs={{setShowJobs, uploadType: uploadTypes.TravelBusi}}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        shouldEdit={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        }
        showAddOnly
        onSelectAll={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? handleSelectAll
            : undefined
        }
        allSelectableCount={dataSource.length}
        onDeselectAll={() => {
          setSelectedRowKeys([]);
        }}
        onChangeStatus={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? bulkChecker.openChangeStatus
            : undefined
        }
        onBulkDelete={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? bulkChecker.openBulkDelete
            : undefined
        }
      />
      {bulkChecker.bulkCheckerModals}
      <JobsModal
        open={showJobs}
        onCancel={() => {
          setShowJobs(false);
          setRefresh(true);
        }}
        type={uploadTypes.TravelBusi}
        details={`${facilityId}/${selectedYear}`}
      />

      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        rowSelection={createBulkRowSelection<DataType>({
          selectedRowKeys,
          setSelectedRowKeys,
          onSelectAll: handleSelectAll,
          onDeselectAll: () => {
            setSelectedRowKeys([]);
          },
          enabled:
            hasUserRole(UserRole.Maker_Role) ||
            hasUserRole(UserRole.Both_role)
        })}
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
        tableLayout="auto"
      />
      <DrawerForm
        open={showModel}
        onCancel={() => {
          setShowModel(false);
        }}
        setRefresh={setRefresh}
        data={isEdit ? editData : undefined}
        FormComponent={
          <BusinessForm
            setFormSelectedCountry={setFormSelectedCountry}
            formSelectedCountry={formSelectedCountry}
          />
        }
        API={businessTravelAPI}
        idKey={`${facilityId}/${selectedYear}`}
        table={true}
        stepsTitle={['Configure Details', 'Employee Information']}
        formPreview={businessTravelKeyLabel}
      />

      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo
            formData={editData}
            formPreview={businessTravelKeyLabel}
          />
        }
        API={businessTravelAPI}
        isChecker={true}
        stepsTitle={['Status & Comments']}
        isAction={false}
        // formPreview={businessTravelKeyLabel}
      />
    </div>
  );
};

export default BusTravelTable;
